/**
 * Console Logger Service
 * 
 * Intercepts and stores console log statements and logSessionInfo calls for debugging purposes.
 * Captures log, warn, error, info messages and session logs with timestamps.
 * Provides functionality to export captured logs to a file.
 * 
 * Only captures logs when in debug mode (DEV/STAGE environment or showDebug URL param).
 */

import { isiPad } from "./environment";

class ConsoleLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 3000; // Circular buffer limit
    this.maxMessageLength = 2000; // Truncate very long messages
    this.isInitialized = false;
    this.logIndex = 0; // Use circular buffer approach
    this.debugModeActive = false;
    this.originalLogSessionInfo = null;
    
    // Store original console methods
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info,
    };
  }
  
  /**
   * Checks if debug mode is active
   * Debug mode is active when:
   * - FEATURE_FLAGS_STACK is DEV or STAGE
   * - OR showDebug URL param is '1' or 'true'
   */
  isDebugModeActive() {
    // Check environment stack
    const stack = process.env.FEATURE_FLAGS_STACK;
    if (stack === 'DEV' || stack === 'STAGE') {
      return true;
    }
    
    // Check URL param
    const urlParams = new URLSearchParams(window.location.search);
    const showDebug = urlParams.get('showDebug');
    return showDebug === '1' || showDebug === 'true';
  }
  
  /**
   * Initializes console and logSessionInfo interception to capture logs
   * Only activates if debug mode is enabled
   */
  init() {
    if (this.isInitialized) {
      return;
    }
    
    // Check if debug mode is active
    this.debugModeActive = this.isDebugModeActive();
    
    if (!this.debugModeActive) {
      // Don't intercept console if not in debug mode
      return;
    }
    
    this.isInitialized = true;
    
    // Intercept console.log
    console.log = (...args) => {
      this.captureLog('log', args);
      this.originalConsole.log.apply(console, args);
    };
    
    // Intercept console.warn
    console.warn = (...args) => {
      this.captureLog('warn', args);
      this.originalConsole.warn.apply(console, args);
    };
    
    // Intercept console.error
    console.error = (...args) => {
      this.captureLog('error', args);
      this.originalConsole.error.apply(console, args);
    };
    
    // Intercept console.info
    console.info = (...args) => {
      this.captureLog('info', args);
      this.originalConsole.info.apply(console, args);
    };
  }
  
  /**
   * Intercepts logSessionInfo from the logger service
   * 
   * Note: This is called at module import time, before init() runs.
   * We always return a wrapper that checks debugModeActive at runtime.
   * 
   * @param {Function} originalLogSessionInfo - The original logSessionInfo function
   * @returns {Function} - Wrapped version that captures logs
   */
  interceptLogSessionInfo(originalLogSessionInfo) {
    // Store original function
    this.originalLogSessionInfo = originalLogSessionInfo;
    
    // Always return wrapped version - check debug mode at runtime.
    // Forward all args (…args) so trailing params like TT-1378's bufferIfNoKey
    // reach the original implementation instead of being dropped here.
    return (...args) => {
      const [inType, inData, forceKey = null] = args;
      // Only capture if debug mode is active (checked at runtime, not import time)
      if (this.debugModeActive) {
        this.captureSessionLog(inType, inData, forceKey);
      }

      // Always call original function
      return originalLogSessionInfo(...args);
    };
  }
  
  /**
   * Captures a session log entry
   */
  captureSessionLog(type, data, forceKey) {
    if (!this.debugModeActive) {
      return;
    }
    
    try {
      const timestamp = new Date().toISOString();
      let message = `[SESSION LOG] Type: ${type}`;
      
      if (forceKey) {
        message += `, ForceKey: ${forceKey}`;
      }
      
      // Format the data
      const formattedData = this.formatLogArgument(data);
      message += `\nData: ${formattedData}`;
      
      // Truncate very long messages to prevent memory issues
      if (message.length > this.maxMessageLength) {
        message = message.substring(0, this.maxMessageLength) + '... [truncated]';
      }
      
      // Use circular buffer approach
      const index = this.logIndex % this.maxLogs;
      this.logs[index] = {
        timestamp,
        type: 'session',
        message,
        sequence: this.logIndex,
      };
      
      this.logIndex++;
    } catch (error) {
      this.originalConsole.error('ConsoleLogger session capture failed:', error);
    }
  }
  
  /**
   * Captures a log entry with timestamp and type
   * Uses circular buffer to avoid array shifting and maintain O(1) performance
   * Only captures if debug mode is active
   */
  captureLog(type, args) {
    // Skip capturing if not in debug mode
    if (!this.debugModeActive) {
      return;
    }
    
    try {
      const timestamp = new Date().toISOString();
      let message = args.map(arg => this.formatLogArgument(arg)).join(' ');
      
      // Truncate very long messages to prevent memory issues
      if (message.length > this.maxMessageLength) {
        message = message.substring(0, this.maxMessageLength) + '... [truncated]';
      }
      
      // Use circular buffer approach - overwrite oldest logs instead of shifting
      const index = this.logIndex % this.maxLogs;
      this.logs[index] = {
        timestamp,
        type,
        message,
        sequence: this.logIndex, // Track original order
      };
      
      this.logIndex++;
    } catch (error) {
      // Silently fail to avoid breaking the app if logging fails
      this.originalConsole.error('ConsoleLogger capture failed:', error);
    }
  }
  
  /**
   * Formats a log argument to a string representation
   * Handles circular references and limits depth for performance
   */
  formatLogArgument(arg) {
    if (typeof arg === 'string') {
      return arg;
    }
    
    if (arg === null) {
      return 'null';
    }
    
    if (arg === undefined) {
      return 'undefined';
    }
    
    if (typeof arg === 'object') {
      try {
        // Use a replacer to handle circular references and limit depth
        const seen = new WeakSet();
        if (isiPad()) {
          return JSON.stringify(arg, null, 2);
        }
        return JSON.stringify(arg, (key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
              return '[Circular Reference]';
            }
            seen.add(value);
          }
          return value;
        }, 2);
      } catch (e) {
        // Fallback for objects that can't be stringified
        return `[Object: ${Object.prototype.toString.call(arg)}]`;
      }
    }
    
    return String(arg);
  }
  
  /**
   * Returns all captured logs in chronological order
   * Optimized: O(n) instead of O(n log n) by avoiding sort
   */
  getLogs() {
    // Before buffer wraps: simple slice (O(n))
    if (this.logIndex <= this.maxLogs) {
      return this.logs.slice(0, this.logIndex).filter(log => log !== undefined);
    }
    
    // After buffer wraps: reconstruct chronological order (O(n))
    // The oldest log is at (logIndex % maxLogs), newest is at (logIndex % maxLogs) - 1
    const startIndex = this.logIndex % this.maxLogs;
    const orderedLogs = [];
    
    // Add logs from startIndex to end of array
    for (let i = startIndex; i < this.maxLogs; i++) {
      if (this.logs[i] !== undefined) {
        orderedLogs.push(this.logs[i]);
      }
    }
    
    // Add logs from beginning to startIndex
    for (let i = 0; i < startIndex; i++) {
      if (this.logs[i] !== undefined) {
        orderedLogs.push(this.logs[i]);
      }
    }
    
    return orderedLogs;
  }
  
  /**
   * Exports logs to a downloadable text file
   */
  exportLogs() {
    const sortedLogs = this.getLogs();
    const totalCaptured = Math.min(this.logIndex, this.maxLogs);
    
    const sessionInfo = `Console Logs Export
Generated: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
URL: ${window.location.href}
Total Logs Captured: ${totalCaptured}
Total Logs in Session: ${this.logIndex}
${this.logIndex > this.maxLogs ? `(Showing most recent ${this.maxLogs} logs)` : ''}
Memory Limit: ${this.maxLogs} logs, ${this.maxMessageLength} chars per message

${'='.repeat(80)}

`;
    
    const logContent = sortedLogs.map(log => {
      return `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`;
    }).join('\n\n');
    
    const fullContent = sessionInfo + logContent;
    
    // Create blob and download
    const blob = new Blob([fullContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `console-logs-${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  
  /**
   * Clears all captured logs and resets the index
   */
  clearLogs() {
    this.logs = [];
    this.logIndex = 0;
  }
  
  /**
   * Gets the count of logs by type
   */
  getLogStats() {
    const validLogs = this.logs.filter(log => log !== undefined);
    const stats = {
      total: validLogs.length,
      log: 0,
      warn: 0,
      error: 0,
      info: 0,
      session: 0,
    };
    
    validLogs.forEach(log => {
      if (stats.hasOwnProperty(log.type)) {
        stats[log.type]++;
      }
    });
    
    return stats;
  }
  
  /**
   * Gets estimated memory usage in KB (rough approximation)
   */
  getEstimatedMemoryUsage() {
    const validLogs = this.logs.filter(log => log !== undefined);
    let totalChars = 0;
    
    validLogs.forEach(log => {
      totalChars += log.timestamp.length;
      totalChars += log.type.length;
      totalChars += log.message.length;
    });
    
    // Rough estimate: 2 bytes per character in JavaScript strings
    const estimatedBytes = totalChars * 2;
    return Math.round(estimatedBytes / 1024); // Convert to KB
  }
}

// Create singleton instance
const consoleLogger = new ConsoleLogger();

export default consoleLogger;

